import { Routes, Route, Navigate } from 'react-router-dom'
import { Sidebar } from './components/layout/Sidebar.jsx'
import { Topbar } from './components/layout/Topbar.jsx'
import { ToastContainer } from './components/Toast.jsx'
import { useToast } from './hooks/useToast.js'
import { Dashboard } from './pages/Dashboard.jsx'
import { Assets } from './pages/Assets.jsx'
import { Risks } from './pages/Risks.jsx'
import { Controls } from './pages/Controls.jsx'
import { Scope } from './pages/Scope.jsx'
import { SoA } from './pages/SoA.jsx'
import { Clauses } from './pages/Clauses.jsx'
import { Processes } from './pages/Processes.jsx'
import { Audits } from './pages/Audits.jsx'
import { NonConformity } from './pages/NonConformity.jsx'
import { Documents } from './pages/Documents.jsx'
import { ReviewCenter } from './pages/ReviewCenter.jsx'
import { Training } from './pages/Training.jsx'
import { ControlsLibrary } from './pages/ControlsLibrary.jsx'
import { Bin } from './pages/Bin.jsx'
import { Vulnerabilities } from './pages/Vulnerabilities.jsx'
import { AuditLogs } from './pages/AuditLogs.jsx'
import { Settings } from './pages/Settings.jsx'
import { MyWork } from './pages/MyWork.jsx'

import { useAuth } from './context/AuthContext.jsx'
import { Login } from './pages/Login.jsx'

function NotFound() {
  return (
    <div className="page">
      <div className="empty-state" style={{ paddingTop: 80 }}>
        <h2 style={{ color: 'var(--text-muted)' }}>Page not found</h2>
        <p>This module is coming in a future phase.</p>
        <a href="/" className="btn btn-primary" style={{ marginTop: 12 }}>← Back to Dashboard</a>
      </div>
    </div>
  )
}


function ProtectedRoute({ user, allowedRoles, children }) {
  if (!user || !allowedRoles.includes(user.role)) {
    return (
      <div className="page">
        <div className="empty-state" style={{ paddingTop: 80 }}>
          <h2 style={{ color: 'var(--danger)' }}>Access Denied</h2>
          <p>You do not have permission to view this compliance module.</p>
          <a href="/" className="btn btn-primary" style={{ marginTop: 12 }}>← Back to Dashboard</a>
        </div>
      </div>
    )
  }
  return children
}

export default function App() {
  const { toasts, toast } = useToast()
  const { user, isAuthenticated, loading, error } = useAuth()

  if (loading) {
    return (
      <div style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh', 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        background: 'var(--bg-base)',
        zIndex: 9999,
        gap: '20px'
      }}>
        <div className="loader"></div>
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: 500 }}>Initializing Platform...</div>
        <style>{`.loader { border: 3px solid var(--border); border-top: 3px solid var(--accent); border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; } @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  if (!isAuthenticated || error) {
    return <Login error={error} />
  }

  const grcRoles = ['Admin', 'Manager', 'Auditor']
  const adminManagerRoles = ['Admin', 'Manager']
  const allRoles = ['Admin', 'Manager', 'Auditor', 'Contributor', 'User']

  return (
    <div className="main-layout">
      <Sidebar user={user} />
      <div className="main-content">
        <Topbar user={user} />
        <div className="content-area">
          <Routes>
            <Route path="/" element={
              (user?.role === 'Contributor' || user?.role === 'User') 
                ? <Navigate to="/my-work" replace /> 
                : <Dashboard user={user} />
            } />
            <Route path="/my-work" element={<MyWork user={user} />} />
            
            {/* Restricted GRC Core / Compliance Routes */}
            <Route path="/scope" element={<ProtectedRoute user={user} allowedRoles={grcRoles}><Scope toast={toast} user={user} /></ProtectedRoute>} />
            <Route path="/processes" element={<Processes toast={toast} user={user} />} />
            <Route path="/assets" element={<ProtectedRoute user={user} allowedRoles={grcRoles}><Assets toast={toast} user={user} /></ProtectedRoute>} />
            <Route path="/vulnerabilities" element={<ProtectedRoute user={user} allowedRoles={allRoles}><Vulnerabilities toast={toast} user={user} /></ProtectedRoute>} />
            <Route path="/risks" element={<ProtectedRoute user={user} allowedRoles={allRoles}><Risks toast={toast} user={user} /></ProtectedRoute>} />
            <Route path="/controls-library" element={<ProtectedRoute user={user} allowedRoles={allRoles}><ControlsLibrary toast={toast} user={user} /></ProtectedRoute>} />
            <Route path="/controls" element={<ProtectedRoute user={user} allowedRoles={allRoles}><Controls toast={toast} user={user} /></ProtectedRoute>} />
            <Route path="/soa" element={<ProtectedRoute user={user} allowedRoles={grcRoles}><SoA toast={toast} user={user} /></ProtectedRoute>} />
            <Route path="/clauses" element={<ProtectedRoute user={user} allowedRoles={grcRoles}><Clauses toast={toast} user={user} /></ProtectedRoute>} />
            <Route path="/audits" element={<ProtectedRoute user={user} allowedRoles={['Admin', 'Auditor']}><Audits toast={toast} user={user} /></ProtectedRoute>} />
            <Route path="/non-conformity" element={<ProtectedRoute user={user} allowedRoles={grcRoles}><NonConformity toast={toast} user={user} /></ProtectedRoute>} />
            <Route path="/documents" element={<Documents toast={toast} user={user} />} />
            <Route path="/review-center" element={<ProtectedRoute user={user} allowedRoles={adminManagerRoles}><ReviewCenter toast={toast} user={user} /></ProtectedRoute>} />
            
            {/* Training module - Disabled/Hidden for everyone */}
            <Route path="/training" element={<NotFound />} />
            
            {/* Admin / Manager Bin */}
            <Route path="/recycle-bin" element={<ProtectedRoute user={user} allowedRoles={adminManagerRoles}><Bin toast={toast} user={user} /></ProtectedRoute>} />
            
            {/* Admin Only Routes */}
            <Route path="/audit-logs" element={<ProtectedRoute user={user} allowedRoles={['Admin']}><AuditLogs toast={toast} user={user} /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute user={user} allowedRoles={['Admin']}><Settings toast={toast} user={user} /></ProtectedRoute>} />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </div>
      <ToastContainer toasts={toasts} />
    </div>
  )
}
